import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/auth';

/**
 * Route guard per le pagine che richiedono un utente autenticato.
 *
 * Decision tree (identico a RequireAdmin, senza il check del ruolo):
 * - !authResolved → spinner (attende il check sessione; è anche lo stato SSR)
 * - authResolved && !hasSession → redirect a /auth/login (con `from` per il ritorno)
 * - hasSession → render children
 *
 * 🛡️ SSR-safe: durante il render server `authResolved` è false → viene reso
 * sempre lo spinner, quindi client e server producono lo stesso markup.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { authResolved, hasSession } = useAuthContext();
  const location = useLocation();

  if (!authResolved) {
    return <AuthLoadingSpinner />;
  }

  if (!hasSession) {
    return <Navigate to="/auth/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

function AuthLoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Verifica accesso in corso...</p>
      </div>
    </div>
  );
}

export default RequireAuth;
